import { FC, ReactNode } from 'react'
import styled from 'styled-components'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media, mediaMax } from 'styles/media'
import typeStyle from 'styles/typeStyle'

import Avatar from '../display/Avatar'
import LazyImage from '../display/LazyImage'
import LinkOrA from '../cards/shared/LinkOrA'

import HorizontallyScrollable from './HorizontallyScrollable'

export const Heading = styled.span`
  ${typeStyle('-ds-font-section-title-regular')}
  color: ${({ theme }) => theme.brand.jungle};
`

const AffiliationWrapper = styled.div``

const AffiliationSpan = styled.span`
  ${typeStyle('-ds-font-lead')}
  color: ${({ theme }) => theme.colors.textGreyDarker};
`

const CTAWrapper = styled.div``

export const Card = styled.div`
  min-width: 220px;
  max-width: 390px; /* to fit 3 in typical layout <Container /> on desktop */

  ${mediaMax.sm`
    width: 66vw;
  `}

  flex-shrink: 0;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  padding: 30px;
  border: 1px solid #dddddd;
  border-radius: 8px;

  ${Avatar} {
    margin-bottom: 16px;
  }

  ${Avatar} {
    height: 90px;
    width: 90px;
    flex-shrink: 0;

    ${media.md`
      height: 120px;
      width: 120px;
    `}
  }

  p {
    color: #000000;
    ${media.md`
      margin-bottom: 40px;
    `}
  }

  p,
  ${CTAWrapper} {
    ${typeStyle('-ds-font-lead-sm-light')}
    margin-top: 16px;
  }

  ${CTAWrapper} {
    margin-top: auto;
    a span {
      text-decoration: underline;
    }
  }
`

type TestimonialData = {
  name: string
  avatar: Partial<GQL_UserAvatar> & { thumbMd: string }
  affiliation?: {
    text: string
    link?: string
  }
  text: ReactNode
  CTA?: ReactNode
}

const Affiliation: FC<{
  text?: string
  link?: string
}> = ({ text, link }) => {
  const { md: largerThanMd } = useMediaQueryContext()
  const isSmallerThanMd = !largerThanMd

  if (!text) {
    return null
  }

  if (link) {
    return (
      <LinkOrA asLink={isSmallerThanMd} to={link}>
        <AffiliationSpan>{text}</AffiliationSpan>
      </LinkOrA>
    )
  }

  return <AffiliationSpan>{text}</AffiliationSpan>
}

type TestimonialsProps = {
  testimonials: TestimonialData[]
}

const Testimonials: FC<TestimonialsProps> = ({
  testimonials: allTestimonials,
}) => {
  const { md: largerThanMd } = useMediaQueryContext()
  const isSmallerThanMd = !largerThanMd

  const testimonials = isSmallerThanMd
    ? allTestimonials
    : allTestimonials.slice(0, 3)

  return (
    <HorizontallyScrollable>
      {testimonials.map(({ name, avatar, affiliation, text, CTA }) => {
        return (
          <Card key={name}>
            <Avatar>
              <LazyImage data-src={avatar.thumbMd} preload />
            </Avatar>
            <Heading>{name}</Heading>
            {affiliation && (
              <AffiliationWrapper>
                <Affiliation {...affiliation} />
              </AffiliationWrapper>
            )}
            <p>{text}</p>
            {CTA && <CTAWrapper>{CTA}</CTAWrapper>}
          </Card>
        )
      })}
    </HorizontallyScrollable>
  )
}

export default Testimonials
